import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Actor, Issue, Label, Priority, StateSlug } from '../api/types'
import { PlanStatusIcon, PlusIcon, PriorityIcon, StateIcon } from '../components/Icons'
import { sameKey } from '../lib/keys'
import { AddToPlan, ChangePlace } from '../plans/AddToPlan'
import { comparePlans } from '../plans/state'
import { placeWords } from '../plans/words'
import { planPath } from '../projects/routing'
import { Popover } from '../components/Popover'
import { ReleaseChips } from '../components/ReleaseChips'
import { releasedOf, releaseSurfaces, surfaceNamesOf } from '../lib/releases'
import { Time } from '../components/Time'
import { useBoardState, useStore } from '../store/context'
import { IssueParent } from './IssueSubIssues'
import { senderName } from '../sessions/words'
import { columnState, ownColumns, placeOf, PRIORITY_ORDER, priorityName, STATES } from '../vocab'

/** Asked before claiming, offline, an issue the local view shows unclaimed (`offline-work.md` §6, Poulpe-826). */
export const OFFLINE_CLAIM_QUESTION = "Poulpe's server is offline. Someone else may claim this issue meanwhile; a claim now is provisional. Claim it provisionally?"

/** Today's property column, using the new model's labels and claim. */
export function IssueProperties({ issue }: { issue: Issue }) {
  const store = useStore()
  const { labels, me, plans, projects, offline, provisionalClaims } = useBoardState()
  // The plans that list it, whatever their project.
  const listing = useMemo(() => [...plans.values()].filter((plan) => plan.issues.some((listed) => sameKey(listed.key, issue.key))).sort(comparePlans), [plans, issue.key])
  const own = ownColumns(projects.get(issue.project))
  const [changingClaim, setChangingClaim] = useState(false)
  const changeClaim = async () => {
    if (changingClaim || !me) return
    if (!issue.claim && offline && !window.confirm(OFFLINE_CLAIM_QUESTION)) return
    setChangingClaim(true)
    try {
      await (issue.claim ? store.releaseIssue(issue.id) : store.claimIssue(issue.id))
    } finally {
      setChangingClaim(false)
    }
  }
  const available = [...labels.values()]
    .filter((label) => label.project === issue.project && label.deleted_at === null)
    .sort((a, b) => a.name.localeCompare(b.name))
  const applied = issue.labels.map((id) => labels.get(id))
    .filter((label): label is Label => !!label && label.project === issue.project && label.deleted_at === null)
  // What its landed work touches, as on its card. A surface that shipped it is one it touches, and its chip says the
  // version (Poulpe-205, Poulpe-500).
  const released = releasedOf(issue)
  const release = releaseSurfaces({
    touches: [...(issue.touches ?? []), ...released.map((entry) => entry.surface)],
    awaits: issue.awaits ?? [],
  }, surfaceNamesOf(projects.get(issue.project)))
  return (
    <dl className="props">
      <div className="prop">
        <dt>State</dt>
        <dd className="prop-control">
          <StateIcon state={issue.state} />
          <select aria-label="State" value={placeOf(issue, own)} onChange={(e) => {
            const column = own.find((c) => c.id === e.target.value)
            void (column ? store.moveIssue(issue.id, columnState(column), undefined, column.id) : store.moveIssue(issue.id, e.target.value as StateSlug))
          }}>
            {STATES.flatMap((s) => [
              <option key={s.slug} value={s.slug}>{s.name}</option>,
              ...own.filter((c) => columnState(c) === s.slug).map((c) => <option key={c.id} value={c.id}>{c.name}</option>),
            ])}
          </select>
        </dd>
      </div>
      <div className="prop">
        <dt>Priority</dt>
        <dd className="prop-control">
          <PriorityIcon priority={issue.priority} />
          <select
            aria-label="Priority"
            value={issue.priority}
            onChange={(e) => void store.updateIssue(issue.id, { priority: e.target.value as Priority })}
          >
            {PRIORITY_ORDER.map((p) => (
              <option key={p} value={p}>
                {priorityName(p)}
              </option>
            ))}
          </select>
        </dd>
      </div>
      <IssueParent issue={issue} />
      <div className="prop">
        <dt>Labels</dt>
        <dd className="prop-labels">
          {applied.map((label) => (
            <span key={label.id} className="label-chip" title={label.name}>
              <span className="label-dot" style={{ background: label.colour }} />
              <span className="label-name">{label.name}</span>
              <button type="button" className="label-remove" aria-label={`Remove label ${label.name}`} onClick={() => void store.removeLabel(issue.id, label.id)}>×</button>
            </span>
          ))}
          <Popover label="Labels" buttonClassName="button-small button-quiet" button={<><PlusIcon size={12} /> Label</>}>
            {() => (
              <div className="menu">
                {available.map((label) => (
                  <label key={label.id} className="menu-check">
                    <input type="checkbox" checked={issue.labels.includes(label.id)} onChange={() => void (issue.labels.includes(label.id) ? store.removeLabel(issue.id, label.id) : store.addLabel(issue.id, label.id))} />
                    <span className="label-dot" style={{ background: label.colour }} />
                    {label.name}
                  </label>
                ))}
                {available.length === 0 && <p className="menu-empty">No labels yet</p>}
              </div>
            )}
          </Popover>
        </dd>
      </div>
      {release.length > 0 && (
        <div className="prop">
          <dt>Release</dt>
          <dd>
            <ReleaseChips surfaces={release} versions={released} />
          </dd>
        </div>
      )}
      <div className="prop">
        <dt>{placeWords.plans}</dt>
        <dd className="prop-list">
          {listing.length === 0 && <span className="muted">{placeWords.noPlan}</span>}
          {listing.map((plan) => (
            <div key={plan.id} className="plan-listed">
              <Link to={planPath(projects.get(plan.project)?.key ?? '', plan.id)} className="prop-link" title={plan.summary || plan.title}>
                <PlanStatusIcon status={plan.status} />
                {plan.title}
              </Link>
              <ChangePlace issue={issue} plan={plan} />
            </div>
          ))}
          <AddToPlan issue={issue} />
        </dd>
      </div>
      <div className="prop">
        <dt>Claim</dt>
        <dd className="prop-claim">
          {issue.claim ? (
            <span title={claimTitle(issue.claim.by, me?.member)}>
              {senderName(issue.claim.by, me)} · <Time iso={issue.claim.at} />
              {provisionalClaims.has(issue.id) && <> · <span className="muted">Provisional claim</span></>}
            </span>
          ) : <span className="muted">Unclaimed</span>}
          <button type="button" className="button button-small" disabled={!me || changingClaim} onClick={() => void changeClaim()}>
            {!issue.claim ? 'Claim' : sameHolder(issue.claim.by, me?.actor) ? 'Unclaim' : 'Release claim'}
          </button>
        </dd>
      </div>
      <div className="prop">
        <dt>Created</dt>
        <dd>
          <Time iso={issue.created_at} />
          {issue.historical_creator ? <> by {issue.historical_creator}</> : issue.created_by && <> by {senderName(issue.created_by, me)}</>}
        </dd>
      </div>
      <div className="prop"><dt>Updated</dt><dd><Time iso={issue.updated_at} /></dd></div>
      {issue.started_at && <div className="prop"><dt>Started</dt><dd><Time iso={issue.started_at} /></dd></div>}
      {issue.completed_at && <div className="prop"><dt>Done</dt><dd><Time iso={issue.completed_at} /></dd></div>}
      {issue.cancelled_at && <div className="prop"><dt>Closed</dt><dd><Time iso={issue.cancelled_at} /></dd></div>}
    </dl>
  )
}

export function sameHolder(holder: Actor, actor: Actor | undefined): boolean {
  if (!actor || holder.kind !== actor.kind) return false
  if (holder.kind === 'person' && actor.kind === 'person') return holder.member === actor.member
  if (holder.kind === 'agent' && actor.kind === 'agent') return holder.session === actor.session
  return holder.kind === 'poulpe'
}

/** The API has no directory of other members; keep the claim's identity on hover. */
function claimTitle(actor: Actor, member: { id: string; name: string } | null | undefined): string | undefined {
  if (actor.kind === 'person' && actor.member !== member?.id) return actor.member
  if (actor.kind === 'agent') return `Session ${actor.session}`
  return undefined
}
